// PR-16.3 — pure helpers for the sponsor file-upload UX. Kept in lib/
// so vitest can import without the supabase client. Used by
// <SponsorLogoUpload> in LeagueForm.
//
// Design:
//   - File validation runs client-side BEFORE the upload to avoid
//     burning a round-trip on a 5 MB file the bucket would have
//     rejected anyway.
//   - Path scheme: logos/leagues/<league.id>/sponsor_<variant>.<ext>
//     Uploads use upsert: true so re-uploading same variant replaces
//     the previous file (same URL after `getPublicUrl` modulo the
//     ?v=timestamp cache-buster the caller appends).
//   - "Ta bort" deletes the storage object only when the URL clearly
//     points at our bucket (heuristic match on the public-URL prefix);
//     pasted external URLs are merely cleared from the form field.

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024; // 2 MiB
const ACCEPTED_MIME_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;

export type SponsorVariant = "my_series" | "register";

export interface ValidationOk {
  ok: true;
  ext: "png" | "jpg" | "jpeg" | "webp";
}

export interface ValidationErr {
  ok: false;
  /** Stable error key — caller maps to localized text via i18n. */
  error: "too_large" | "wrong_type" | "no_file";
}

/**
 * Validates a File picked by the admin against the sponsor-logo
 * constraints (≤ 2 MiB, PNG / JPEG / WebP). Returns a stable error key
 * so the caller can render localized text without coupling this lib
 * to i18next.
 *
 * Also returns the canonical extension to use in the storage path
 * (jpeg → "jpg" so we don't end up with two paths for the same MIME).
 */
export function validateSponsorImageFile(
  file: File | null | undefined,
): ValidationOk | ValidationErr {
  if (!file) return { ok: false, error: "no_file" };
  if (file.size > MAX_FILE_SIZE_BYTES) return { ok: false, error: "too_large" };
  if (!ACCEPTED_MIME_TYPES.includes(file.type as typeof ACCEPTED_MIME_TYPES[number])) {
    return { ok: false, error: "wrong_type" };
  }
  // Map MIME → canonical ext
  const ext: ValidationOk["ext"] =
    file.type === "image/png"
      ? "png"
      : file.type === "image/webp"
        ? "webp"
        : "jpg"; // image/jpeg → "jpg"
  return { ok: true, ext };
}

/**
 * Computes the storage object path for a sponsor logo upload.
 *
 *   logos/leagues/<leagueId>/sponsor_my_series.png
 *   logos/leagues/<leagueId>/sponsor_register.webp
 *
 * Note: the leading "logos/" comes from `supabase.storage.from("logos")`
 * — this helper returns just the object key inside that bucket.
 */
export function sponsorStoragePath(
  leagueId: string,
  variant: SponsorVariant,
  ext: ValidationOk["ext"],
): string {
  return `leagues/${leagueId}/sponsor_${variant}.${ext}`;
}

/**
 * Heuristic: does this URL look like it points at our logos bucket?
 * Used by the "Ta bort" handler to decide whether to delete the
 * storage object (yes → delete + clear field) or merely clear the
 * field (no → it's a pasted external URL, leave any external file
 * alone).
 *
 * Matches "/storage/v1/object/public/logos/" anywhere in the URL,
 * which is the standard supabase public-URL prefix. If the URL was
 * uploaded via this app it'll have that segment.
 */
export function isOurLogosBucketUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return /\/storage\/v1\/object\/public\/logos\//i.test(url);
}

/**
 * Extracts the storage object key (path inside the bucket) from a
 * supabase public URL of the form
 *   https://xxx.supabase.co/storage/v1/object/public/logos/leagues/<id>/sponsor_<variant>.png
 *
 * Returns null if the URL doesn't match the expected shape.
 *
 * Used by the "Ta bort" handler to call `supabase.storage.from("logos").remove([key])`.
 */
export function extractLogosBucketKey(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = url.match(/\/storage\/v1\/object\/public\/logos\/(.+?)(?:\?|$)/i);
  return m ? decodeURIComponent(m[1]) : null;
}
